import { useEffect, useState, useMemo } from 'react'; // <-- Import useMemo
import { ValidDataService } from '../../../service/validDataService';

export const useValidInventoryColumns = (type) => {
    const [locations, setLocations] = useState([]);
    const [brands, setBrands] = useState([]);
    const [materials, setMaterials] = useState([]);
    // New states for frame-specific filters
    const [shapes, setShapes] = useState([]);
    const [lensType, setLensType] = useState([]);

    useEffect(() => {
        // Helper function to fetch filter options from new dedicated endpoints
        const fetchFilterOptions = async (serviceMethod, setStateFunction, property) => {
            try {
                const response = await serviceMethod(type);
                const { code, data } = response;
                if (code === 0) {
                    // Map backend response to filter options format
                    const options = data.map(item => ({
                        value: item.id,
                        label: item.name
                    }));
                    setStateFunction(options);
                }
            } catch (error) {
                console.error(`Error fetching ${property}:`, error);
            }
        };

        const fetchPropertyOptions = async (serviceMethod, setStateFunction, property) => {
            try {
                const response = await serviceMethod(type, property);
                const { code, data } = response;
                if (code === 0) {
                    const options = data.map(item => ({
                        value: item.value,
                        label: item.label
                    }));
                    setStateFunction(options);
                }
            } catch (error) {
                console.error(`Error fetching ${property}:`, error);
            }
        };

        // Only fetch if a type is provided
        if (type) {
            // Fetch common filters using new dedicated endpoints
            fetchFilterOptions(ValidDataService.getInventoryLocationsFilter, setLocations, 'locations');
            fetchFilterOptions(ValidDataService.getInventoryBrandsFilter, setBrands, 'brands');
            fetchFilterOptions(ValidDataService.getFrameMaterialsFilter, setMaterials, 'materials');

            // Fetch frame-specific filters only if the type is 'frame'
            if (type === 'frame') {
                fetchFilterOptions(ValidDataService.getFrameShapesFilter, setShapes, 'shapes');
                setLensType([]);

            } else if (type === 'lens') {
                // Clear frame-specific filters if type is not 'frame' to avoid stale data
                setShapes([]);
                fetchPropertyOptions(ValidDataService.getPropertyFilterOptions, setLensType, 'lensType');
            }
        } else {
            // Clear all states if type is not provided
            setLocations([]);
            setBrands([]);
            setMaterials([]);
            setShapes([]);
        }

    }, [type]); // <-- CRITICAL: Add 'type' to the dependency array.
    // This ensures fetches re-run when the inventory type changes.

    // Use useMemo to define columns. This prevents unnecessary re-creation of the
    // columns array on every render, improving performance for EnhancedTable.
    const columns = useMemo(() => {
        // Define all possible column definitions
        const columnDefinitions = {
            manufacturer: {
                Header: 'Manufacturer',
                accessor: (row) => row.manufacturer?.name || '',
                id: 'manufacturer',
                enableFilter: true,
                type: 'text'
            },
            brand: {
                Header: 'Brand',
                accessor: (row) => {
                    // For lenses, use manufacturerBrand
                    if (type === 'lens' && row.manufacturerBrand?.brandName) {
                        return row.manufacturerBrand.brandName;
                    }
                    // For frames, use catalog brand or legacy brand
                    return row.catalogProduct?.brand?.name || row.brand?.name || '';
                },
                id: 'brand',
                enableFilter: true,
                type: 'enum',
                filterOptions: brands
            },
            modelNumber: {
                Header: 'Model Number',
                accessor: (row) => (row.catalogProduct?.frame?.modelNumber || row.modelNumber || '').toUpperCase(),
                id: 'modelNumber',
                enableFilter: true,
                type: 'text'
            },
            sku: {
                Header: 'SKU',
                accessor: 'sku',
                enableFilter: true,
            },
            material: {
                Header: 'Material',
                accessor: (row) => {
                    // For catalog items, get from catalogProduct.frame.material
                    if (row.catalogProduct?.frame?.material?.name) {
                        return row.catalogProduct.frame.material.name;
                    }
                    // For non-catalog items, use property tables
                    if (type === 'lens') return row.lensproperties?.material;
                    if (type === 'contacts') return row.contactproperties?.material;
                    return '';
                },
                id: 'material',
                enableFilter: true,
                type: 'enum',
                filterOptions: materials
            },
            frameType: {
                Header: 'Frame Type',
                accessor: (row) => row.properties?.frameType || '',
                id: 'frameType',
                enableFilter: true,
                type: 'enum',
                filterOptions: [
                    { value: 'optical', label: 'Optical' },
                    { value: 'sunglasses', label: 'Sunglasses' },
                    { value: 'other', label: 'Other' },
                ]
            },
            color: {
                Header: 'Color',
                accessor: (row) => row.properties?.color || '',
                id: 'color',
                enableFilter: true,
            },
            // shape: {
            //     Header: 'Shape',
            //     accessor: (row) => row.catalogProduct?.frame?.shape?.name || row.properties?.shape || '',
            //     id: 'shape',
            //     enableFilter: true,
            //     type: 'enum',
            //     filterOptions: shapes
            // },
            inStock: {
                Header: 'In Stock',
                accessor: 'quantityOnHand',
                enableFilter: true,
                type: 'number'
            },
            onOrder: {
                Header: 'On Order',
                accessor: 'quantityOnOrder',
                enableFilter: true,
                type: 'number'
            },
            // color: {
            //     Header: 'Color',
            //     accessor: (row) => row.catalogProduct?.frame?.colorName || row.properties?.color || '',
            //     id: 'color',
            //     enableFilter: false,
            // },
            lensType: {
                Header: 'Lens Type',
                accessor: 'lensproperties.lensType',
                enableFilter: true,
                type: 'enum',
                filterOptions: lensType
            },
            addPower: {
                Header: 'Add',
                accessor: 'lensproperties.addPower',
                enableFilter: true,
                type: 'number'
            },
            listPrice: {
                Header: 'List Price',
                accessor: 'listPrice',
                enableFilter: true,
                type: 'number'
            },
            location: {
                Header: 'Location',
                accessor: (row) => row.location?.name || '',
                id: 'location_id',
                enableFilter: true,
                type: 'enum',
                filterOptions: locations
            }
        };

        // Define column ordering per type
        const columnOrder = {
            frame: ['brand', 'modelNumber', 'sku', 'frameType', 'color', 'listPrice', 'inStock', 'onOrder', 'location'],
            lens: ['manufacturer', 'brand', 'lensType', 'material', 'addPower', 'listPrice'],
            contacts: ['material', 'listPrice'],
            accessories: ['listPrice']
        };

        // Build columns array based on type
        const orderedColumnKeys = columnOrder[type] || [];
        const baseColumns = orderedColumnKeys
            .map(key => columnDefinitions[key])
            .filter(Boolean); // Remove any undefined columns

        return baseColumns;
    }, [brands, materials, type, lensType, locations]);

    return columns;
};